import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import { SxProps } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { DatePicker } from '@mui/x-date-pickers-pro';
import { NameIdObj, NameIdObjString } from 'common/models/GenericTypes';
import { JobsNotFilledFilter } from 'common/models/Reporting/AnalyticsFilterData';
import JobsGridComponent from 'components/Grids/JobsGrid';
import MultipleDivisionsPicker from 'components/Pickers/MultipleDivisionPicker';
import MultipleGroupPicker from 'components/Pickers/MultipleGroupPicker';
import MultipleStringValuesPicker from 'components/Pickers/MultipleStringValuesPicker';
import MultipleUserPicker from 'components/Pickers/MultipleUserPicker';
import React, { useCallback, useEffect, useState } from 'react';
import { GetCustomerSettingBySettingName, GetJobsSettings } from 'services/ConfigurationService';
import { GetDatesFromTimeRangeFilter } from 'util/DateUtils';
import { JobTypeOptions } from 'util/Definitions/Jobs';

interface Props {
  description: string;
  loadingHandler?: (isLoading: boolean) => void;
  errorHandler?: (message: string) => void;
}

const filterElementStyle: SxProps = { flex: '1 1 0' };
const defaultTimeRange = 2;
const divisionSpecialOptions: NameIdObj[] = [{ id: 0, name: 'All' }];

export default function JobsNotFilledReport({ description, loadingHandler, errorHandler }: Props) {
  const [sourceType, setSourceType] = useState(1);
  const [jobTypes, setJobTypes] = useState(['All']);
  const [status, setStatus] = useState('All');
  const [divisions, setDivisions] = useState([0]);
  const [selectedGroups, setSelectedGroups] = useState<number[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [timeRange, setTimeRange] = useState(defaultTimeRange);
  const [startDate, setStartDate] = useState<moment.Moment | null>(null);
  const [endDate, setEndDate] = useState<moment.Moment | null>(null);
  const [filterData, setFilterData] = useState<JobsNotFilledFilter>();

  const [isInitialSetupFinished, setIsInitialSetupFinished] = useState(false);
  const [isOnHoldActive, setIsOnHoldActive] = useState(false);
  const [financialYearStartMonth, setFinancialYearStartMonth] = useState(1);

  useEffect(() => {
    const getSetupData = async () => {
      loadingHandler?.(true);
      const [jobSettings, yearStart] = await Promise.all([
        GetJobsSettings(errorHandler),
        GetCustomerSettingBySettingName('YearStart'),
      ]);

      if (jobSettings) {
        const onHoldSetting = jobSettings.find((v) => v.type === 'OnHold');
        if (onHoldSetting) setIsOnHoldActive(onHoldSetting.value === 'True');
      }
      if (yearStart) {
        const parsed = +yearStart;
        if (parsed >= 1 && parsed <= 12) setFinancialYearStartMonth(parsed);
      }

      loadingHandler?.(false);
      setIsInitialSetupFinished(true);
    };
    getSetupData();
  }, [loadingHandler, errorHandler]);

  const setDatesByTimeRange = useCallback(
    (tr: number) => {
      const dates = GetDatesFromTimeRangeFilter(tr, financialYearStartMonth);
      if (dates) {
        const [start, end] = dates;
        setStartDate(start);
        setEndDate(end);
      }
    },
    [financialYearStartMonth]
  );

  useEffect(() => {
    if (isInitialSetupFinished) {
      setDatesByTimeRange(timeRange);
    }
  }, [timeRange, isInitialSetupFinished, setDatesByTimeRange]);

  const runReportCallback = useCallback(() => {
    let sIds: number[] = [];
    if (sourceType === 3) sIds = selectedGroups;
    if (sourceType === 4) sIds = selectedUsers;
    const f: JobsNotFilledFilter = {
      sourceType: sourceType,
      sourceIds: sIds,
      jobTypes: jobTypes.includes('All') ? [] : jobTypes,
      jobStatus: status,
      divisionIds: divisions.includes(0) ? [] : divisions,
      startDate: startDate?.isValid() ? startDate.format('YYYY-MM-DD') : '',
      endDate: endDate?.isValid() ? endDate.format('YYYY-MM-DD') : '',
    };
    setFilterData(f);
  }, [sourceType, selectedGroups, selectedUsers, jobTypes, status, divisions, startDate, endDate]);

  const handleMultiJobTypeChange = useCallback((values: NameIdObjString[]) => {
    const ids = values.map((v) => v.id);
    setJobTypes(ids);
  }, []);

  const handleMultiDivisionChange = useCallback((values: NameIdObj[]) => {
    const ids = values.map((v) => v.id);
    setDivisions(ids);
  }, []);

  const onGroupsChange = useCallback((groups: NameIdObj[]) => {
    setSelectedGroups(groups.map((u) => u.id));
  }, []);

  const onUsersChange = useCallback((users: NameIdObj[]) => {
    setSelectedUsers(users.map((u) => u.id));
  }, []);

  const onTimeRangeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const v = +e.target.value;
    setTimeRange(v);
  }, []);

  return (
    <>
      <Box display="flex" gap={1} mb={1}>
        <TextField
          select
          label="Source Type"
          value={sourceType.toString()}
          onChange={({ target }) => setSourceType(+target.value)}
          sx={filterElementStyle}
        >
          <MenuItem value="1">All</MenuItem>
          <MenuItem value="3">Group</MenuItem>
          <MenuItem value="4">User</MenuItem>
        </TextField>
        <MultipleStringValuesPicker
          label="Type"
          options={JobTypeOptions}
          value={jobTypes}
          onSelect={handleMultiJobTypeChange}
          sx={filterElementStyle}
        />
        <TextField
          select
          label="Status"
          value={status}
          onChange={({ target }) => setStatus(target.value)}
          sx={filterElementStyle}
        >
          <MenuItem value="Open">Open</MenuItem>
          {isOnHoldActive && <MenuItem value="Hold">Hold</MenuItem>}
          <MenuItem value="Closed">Closed</MenuItem>
          <MenuItem value="All">All</MenuItem>
        </TextField>
        <MultipleDivisionsPicker
          value={divisions}
          appendToStart={divisionSpecialOptions}
          onSelect={handleMultiDivisionChange}
          sx={filterElementStyle}
        />
        <Button variant="contained" color="success" onClick={runReportCallback}>
          Run Report
        </Button>
      </Box>
      <Box display={sourceType === 3 ? undefined : 'none'} mb={1}>
        <MultipleGroupPicker value={selectedGroups} onSelect={onGroupsChange} />
      </Box>
      <Box display={sourceType === 4 ? undefined : 'none'} mb={1}>
        <MultipleUserPicker includeInactiveUsers value={selectedUsers} onSelect={onUsersChange} />
      </Box>
      <Box display="flex" gap={1}>
        <TextField
          select
          label="Time Range"
          value={timeRange.toString()}
          onChange={onTimeRangeChange}
          sx={filterElementStyle}
        >
          <MenuItem value="0">Custom Range</MenuItem>
          <MenuItem value="1">This Week</MenuItem>
          <MenuItem value="2">This Month</MenuItem>
          <MenuItem value="3">This Quarter (Calendar)</MenuItem>
          <MenuItem value="13">This Quarter (Financial)</MenuItem>
          <MenuItem value="4">This Year (Calendar)</MenuItem>
          <MenuItem value="14">This Year (Financial)</MenuItem>
          <MenuItem value="5">Last Week</MenuItem>
          <MenuItem value="6">Last Month</MenuItem>
          <MenuItem value="7">Last Quarter (Calendar)</MenuItem>
          <MenuItem value="15">Last Quarter (Financial)</MenuItem>
          <MenuItem value="8">Last Year (Calendar)</MenuItem>
          <MenuItem value="16">Last Year (Financial)</MenuItem>
          <MenuItem value="9">Last 7 Days</MenuItem>
          <MenuItem value="10">Last 30 Days</MenuItem>
          <MenuItem value="11">Last 90 Days</MenuItem>
          <MenuItem value="12">Last 365 Days</MenuItem>
        </TextField>
        <DatePicker
          label="From Date"
          value={startDate}
          onChange={setStartDate}
          disabled={timeRange !== 0}
          sx={filterElementStyle}
          slotProps={{ actionBar: { actions: ['clear', 'today', 'cancel', 'accept'] } }}
        />
        <DatePicker
          label="To Date"
          value={endDate}
          onChange={setEndDate}
          disabled={timeRange !== 0}
          sx={filterElementStyle}
          slotProps={{ actionBar: { actions: ['clear', 'today', 'cancel', 'accept'] } }}
        />
      </Box>
      <Typography component="div" variant="h6" mt={2}>
        Report Description
      </Typography>
      <Typography component="div" variant="body2">
        {description}
      </Typography>
      {filterData && (
        <JobsGridComponent
          gridName="jobs-not-filled-report"
          source="jobs-not-filled-report"
          filterData={filterData}
          loadingHandler={loadingHandler}
          errorHandler={errorHandler}
          jobState={0}
        />
      )}
    </>
  );
}
